import React, { useEffect } from "react";
import { X } from "lucide-react";

const NAVY = "#1C4E80";
const CORAL = "#EA6A47";
const SLATE = "#5A6B75";

const EXAMPLES = [
  ["The roof you may never replace", "Pay for what you use.", "Reserve contributions aren't only payments for future repairs. They pay for the portion of an asset's useful life each of us uses today."],
  ["The home you leave your children", "Preserve property value.", "A well-funded reserve helps protect the value of your property, whether you sell it, pass it on, or stay for the rest of your life."],
  ["The footbridge and the fireworks", "Preserve what earlier generations built.", "As a child, you may have crossed the little footbridge over the creek to watch the Association's Fourth of July fireworks. Decades later, your children and grandchildren cross that same aging bridge for the same tradition. Your parents' generation helped pay for the bridge you enjoyed; today we can help preserve it for those who come next. Reserve funding sustains the traditions and shared experiences that make a community special."],
  ["The bill that arrives for the next owner", "Generational equity.", "Each group of owners pays its fair share as it goes, rather than passing yesterday's costs to tomorrow's residents."],
];

const IDEAS = [
  ["Use it. Pay for it.", "Every owner uses up a portion of the useful life of our common assets."],
  ["Own it. Protect it.", "Adequate reserves help preserve the marketability and value of everyone's property."],
  ["Enjoy it. Preserve it.", "Each generation should leave the community financially prepared for the next."],
];

const label = { fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 700, color: NAVY, margin: "22px 0 10px" };

// Condensed one-page "Why" guide — shared by the landing pop-up and the member report.
export function WhyReserveCondensed({ showStatutory = false }) {
  return (
    <div style={{ fontFamily: "'Source Sans 3', sans-serif", color: "#1A1A1A", fontSize: 14, lineHeight: 1.5 }} data-testid="why-reserve-condensed">
      <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 600, color: SLATE }}>
        An owner resource from your Association&rsquo;s Board of Directors
      </div>
      <div style={{ marginTop: 6, fontSize: 28, lineHeight: 1.15, color: NAVY, fontFamily: "'Source Serif 4', Georgia, serif" }}>
        The <span style={{ color: CORAL, fontStyle: "italic" }}>Why</span> of Reserve Funding
      </div>
      <div style={{ marginTop: 2, fontSize: 16, color: NAVY }}>Our Fair Share, Together</div>
      <div style={{ marginTop: 12, fontSize: 15, fontStyle: "italic", color: CORAL }}>
        You don&rsquo;t have to be here when it&rsquo;s replaced to have benefited from it.
      </div>
      <p style={{ marginTop: 12 }}>
        Our roofs, roads, and amenities age a little every year, whether or not we notice. Reserve funding lets each
        owner contribute a fair share for the benefits received today, so replacements are paid for steadily over time
        instead of arriving as a surprise.
      </p>

      <div style={label}>Four ways to think about it</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {EXAMPLES.map(([title, principle, takeaway], i) => (
          <div key={title} style={{ paddingLeft: 12, borderLeft: `3px solid ${CORAL}` }}>
            <div style={{ fontWeight: 600, color: NAVY }}>
              {i + 1}. {title} <span style={{ fontWeight: 400, fontStyle: "italic", color: SLATE }}>&mdash; {principle}</span>
            </div>
            <div>{takeaway}</div>
          </div>
        ))}
      </div>

      <div style={label}>Three simple ideas to remember</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
        {IDEAS.map(([t, d]) => (
          <div key={t} style={{ borderRadius: 4, padding: 10, background: "#EEF5FB", border: "0.5px solid #88BFDE" }}>
            <div style={{ fontWeight: 600, color: NAVY }}>{t}</div>
            <div style={{ fontSize: 13, marginTop: 3 }}>{d}</div>
          </div>
        ))}
      </div>

      {showStatutory && (
        <>
          <div style={label}>Statutory Requirement</div>
          <div style={{ paddingLeft: 12, borderLeft: `3px solid ${NAVY}` }} data-testid="why-reserve-statutory">
            All states have statutory provisions that require common interest communities to set aside funds for the
            future replacement of Common Elements. Using planned Special Assessments in lieu of regular reserve funding
            is prohibited.
          </div>
        </>
      )}
    </div>
  );
}

export default function WhyReserveModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 42, 68, 0.45)" }}
      onClick={onClose}
      data-testid="why-reserve-modal"
    >
      <div
        className="relative bg-white rounded-[6px] w-full max-w-[720px] max-h-[88vh] overflow-y-auto shadow-xl"
        style={{ border: `1px solid ${NAVY}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 transition-colors"
          aria-label="Close"
          data-testid="why-reserve-close"
        >
          <X size={18} color={NAVY} />
        </button>
        <div className="px-8 pt-8 pb-8">
          <WhyReserveCondensed />
        </div>
      </div>
    </div>
  );
}
